import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useActivityDetection Hook
 * 
 * Tracks real user activity (mouse, keyboard, scroll, touch) to determine
 * if the user is actively using the application. This is the core of the
 * inactivity timeout system.
 * 
 * Features:
 * - Throttled event detection (prevents excessive updates)
 * - Cross-tab activity awareness
 * - Automatic cleanup on unmount
 * - Configurable activity events
 */

interface UseActivityDetectionOptions {
  /** Events to track as activity (default: mousedown, keydown, scroll, touchstart, click) */
  events?: string[];
  /** Throttle interval in ms (default: 1000) */
  throttleMs?: number;
  /** Enable activity detection (default: true) */
  enabled?: boolean;
}

interface UseActivityDetectionReturn {
  /** Timestamp of last detected activity */
  lastActivity: number;
  /** Whether user is currently active */
  isActive: boolean;
  /** Time in milliseconds since last activity */
  timeSinceActivity: number;
  /** Manually trigger activity (useful for programmatic events) */
  triggerActivity: () => void;
}

export const useActivityDetection = (
  options: UseActivityDetectionOptions = {}
): UseActivityDetectionReturn => {
  const {
    events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'],
    throttleMs = 1000,
    enabled = true,
  } = options;

  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [isActive, setIsActive] = useState<boolean>(true);
  const throttleTimerRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Update activity timestamp
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
    setIsActive(true);
  }, []);

  // Throttled activity handler
  const handleActivity = useCallback(() => {
    if (!enabled) return;

    // If throttle timer is active, skip this event
    if (throttleTimerRef.current !== null) return;

    // Update activity immediately
    updateActivity();

    // Set throttle timer
    throttleTimerRef.current = window.setTimeout(() => {
      throttleTimerRef.current = null;
    }, throttleMs);
  }, [enabled, throttleMs, updateActivity]);

  // Setup activity listeners
  useEffect(() => {
    if (!enabled) return;

    // Add passive listeners for better performance
    const eventOptions = { passive: true };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, eventOptions);
    });

    // Cleanup on unmount
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      // Clear throttle timer
      if (throttleTimerRef.current !== null) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
    };
  }, [enabled, events, handleActivity]);

  // Calculate time since last activity
  const [timeSinceActivity, setTimeSinceActivity] = useState<number>(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const now = Date.now();
      const timeSince = now - lastActivityRef.current;
      setTimeSinceActivity(timeSince);

      // Consider user inactive after 1 minute of no activity
      if (timeSince > 60000) {
        setIsActive(false);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Manual activity trigger
  const triggerActivity = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  return {
    lastActivity,
    isActive,
    timeSinceActivity,
    triggerActivity,
  };
};
